import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Chip, Snackbar, Alert, IconButton,
} from '@mui/material';
import { ExpandLess, ExpandMore, Logout, Chat, Close } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '../contexts/ThemeContext'; // ✅ import theme context
import { getActiveBackgroundColor, getHoverColor } from '../utils/colorUtils'; // optional color helpers
import { logout } from '../Redux/Slices/AuthSlice';
import { toast } from 'react-hot-toast';

import dashboardicon from '../assets/dashboardicon.svg';
import activedashboard from "../assets/activedashboard.svg";
import attendance from "../assets/Sidebar/attendance.svg";
import taskmanagement from "../assets/Sidebar/taskmanagement.svg";
import requestmanagement from "../assets/Sidebar/requestmanagement.svg";
import documents from "../assets/Sidebar/documents.svg";
import reports from "../assets/Sidebar/reports.svg";
import message from "../assets/Sidebar/message.svg";
import payments from "../assets/Sidebar/payments.svg";
import activerequest from '../assets/activerequest.svg';
import notifications from "../assets/Sidebar/notifications.svg";
import accountsettigs from "../assets/Sidebar/accountsettings.svg";
import sidebaricon1 from '../assets/sidebaricon1.svg';
import active from '../assets/active.svg';
import activenotifications from "../assets/activenotifications.svg";
import activereports from "../assets/activereports.svg";
import activesetting from "../assets/activesettings.svg";
import websitelogo from "../assets/websitelogo.png";
import websitelogoes from "../assets/websitelogoes.png";
import DynamicIcon from './DynamicIcon';
import DynamicDropdownIcon from './DynamicDropdownIcon';
import finance from "../assets/Sidebar/finance.svg";
import loan from "../assets/Sidebar/loan.svg";
import lenders from "../assets/Sidebar/lenders.svg"
import { hasTasksAccess, hasFullAccess } from '../utils/planAccess';
const MESSAGE_TOAST_STORAGE_KEY = "worker_message_toast_state";

// Visible on every plan, including the restricted attendance-only plan
const BASE_MENU_KEYS = [
  'dashboard_root', 'attendance', 'notifications', 'settings', 'logout',
  'suppliers', 'vacancies', 'applicants',
];
// Unlocked once the worker's plan includes task management
const TASK_MENU_KEYS = ['task_management'];

const Sidebar = ({ mobile = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { totalCount } = useSelector((state) => state.messageCount);
  const { user } = useSelector((state) => state.auth);
  const planId = user?.plan_id;
  const [showMessageToast, setShowMessageToast] = useState(false);
  const [toastUnreadCount, setToastUnreadCount] = useState(0);
  const previousUnreadRef = useRef(null);

  // ✅ get theme colors
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor, companyLogo } = useTheme();
  const [hoveredItem, setHoveredItem] = useState(null);
  const menuItems = useMemo(() => [
    {
      key: 'dashboard_root',
      label: t("sidebar.dashboard"),
      icon: dashboardicon,
      activeIcon: activedashboard,
      path: '/dashboard',
    },
    {
      key: 'attendance',
      label: t("sidebar.attendance"),
      icon: attendance,
      children: [
        { key: 'attendance_today_punch', label: t("sidebar.todayPunch"), path: '/today-punch' },
        { key: 'attendance_monthly_summary', label: t("sidebar.monthlySummary"), path: '/monthly-summary' },
      ],
    },
    {
      key: 'task_management',
      label: t("sidebar.taskManagement"),
      icon: taskmanagement,
      children: [
        { key: 'task_my_tasks', label: t("sidebar.myTasks"), path: '/my-tasks' },
        { key: 'task_attendance_history', label: t("task_attendance_history"), path: '/task-attendance-history' },
        // { key: 'task_calendar_view', label: t("sidebar.calendarView"), path: '/calender-view' },
      ],
    },
     {
      key: 'messages',
      label: t("sidebar.messages"),
      icon: message,
      path: '/messages'
    
    },
    {
      key: 'notifications',
      label: t("sidebar.notifications"),
      icon: notifications,
      activeIcon: activenotifications,
      path: '/notifications',
    },
    {
      key: 'request_management',
      label: t("sidebar.requestManagements"),
      icon: requestmanagement,
      activeIcon: activerequest,
      path: '/request-management',
    },
    {
      key: 'documents',
      label: t("sidebar.document"),
      icon: documents,
      children: [
        { key: 'documents_mine', label: t("sidebar.myDocuments"), path: '/sent-documents' },
        { key: 'documents_companies', label: t("sidebar.companiesDocs"), path: '/received-documents' },
        { key: 'documents_automated', label: t("automatedDocument"), path: '/automated-documents' },
      ],
    },
    {
      key: 'payments',
      label: t("sidebar.payments"),
      icon: payments,
      children: [
        { key: 'payments_expenses', label: t("sidebar.expenses"), path: '/expenses' },
        { key: 'payments_remuneration', label: t("sidebar.remuneration"), path: '/remuneration' },
      ],
    },
       {
      key: 'report-screen',
      label: t("sidebar.reports"),
      icon:reports,
      activeIcon: activereports,
      path: '/reports',
    },
    {
      key: 'absence',
      label: t("sidebar.absence"),
      icon: attendance,
      activeIcon: attendance,
      path: '/absence',
    },
      // add absenses 
      { key: 'suppliers', label: t('sidebar.finance'), icon:  finance , activeIcon:  finance , path: '/finance' },

    { key: 'vacancies', label: t('sidebar.loans'), icon:loan, activeIcon:  loan, path: '/loan' },
  { key: 'applicants', label: t('sidebar.lenders'), icon: lenders, activeIcon:  lenders, path: '/lenders' },
    
    // {
    //   key: 'reports',
    //   label: t("sidebar.reportStatistics"),
    //   icon: reports,
    //   activeIcon: activereports,
    //   path: '/reports-services',
    // },
   
    
    {
      key: 'settings',
      label: t("sidebar.accountSettings"),
      icon: accountsettigs,
      activeIcon: activesetting,
      path: '/settings',
    },
    {
      key: 'logout',
      label: t("sidebar.logout") || "Logout",
      icon: Logout,
      activeIcon: Logout,
      isLogout: true,
    },
  ], [t]);

  // Gate sidebar modules by the worker's plan_id (returned from the login API)
  const visibleMenuItems = useMemo(() => {
    if (hasFullAccess(planId)) {
      return menuItems;
    }
    if (hasTasksAccess(planId)) {
      return menuItems.filter(
        (item) => BASE_MENU_KEYS.includes(item.key) || TASK_MENU_KEYS.includes(item.key)
      );
    }
    // Attendance/free plan (and any unrecognized/missing plan_id) fall back to the most restricted view
    return menuItems.filter((item) => BASE_MENU_KEYS.includes(item.key));
  }, [menuItems, planId]);

  const [openMenus, setOpenMenus] = useState({});
  const selectedPath = location.pathname;

  useEffect(() => {
    const parent = visibleMenuItems.find(
      (item) => item.children && item.children.some((c) => c.path === selectedPath)
    );
    if (parent) setOpenMenus({ [parent.key]: true });
    else setOpenMenus({});
  }, [selectedPath, visibleMenuItems]);

  const toggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

//   const language = i18n.language === 'es' ? 'Spanish' : 'English';
//   const logoPath = language === 'English' ? websitelogoes : websitelogo;

const language = i18n.language === 'es' ? 'Spanish' : 'English';
const defaultLogo = language === 'English' ? websitelogoes : websitelogo;
const [logoToShow, setLogoToShow] = useState(defaultLogo);
  const [openKey, setOpenKey] = useState(null);
useEffect(() => {
  if (companyLogo && companyLogo.trim() !== '') {
    setLogoToShow(companyLogo);
  } else {
    setLogoToShow(defaultLogo);
  }
}, [companyLogo, defaultLogo]);
console.log(companyLogo)
console.log("sdebar logog",logoToShow)

  useEffect(() => {
    try {
      const savedToastState = sessionStorage.getItem(MESSAGE_TOAST_STORAGE_KEY);
      if (!savedToastState) return;

      const parsedToastState = JSON.parse(savedToastState);
      if (parsedToastState?.open && parsedToastState?.count > 0) {
        setToastUnreadCount(parsedToastState.count);
        setShowMessageToast(true);
      }
    } catch (error) {
      console.warn("Failed to restore message toast state:", error);
    }
  }, []);

  useEffect(() => {
    const isMessagesPage = location.pathname.includes("/messages");

    if (isMessagesPage) {
      setShowMessageToast(false);
      setToastUnreadCount(0);
      sessionStorage.removeItem(MESSAGE_TOAST_STORAGE_KEY);
      previousUnreadRef.current = totalCount || 0;
      return;
    }

    if (previousUnreadRef.current === null) {
      previousUnreadRef.current = totalCount || 0;
      return;
    }

    if ((totalCount || 0) > previousUnreadRef.current) {
      setToastUnreadCount(totalCount || 0);
      setShowMessageToast(true);
    }

    previousUnreadRef.current = totalCount || 0;
  }, [totalCount, location.pathname]);

  useEffect(() => {
    if (!showMessageToast || toastUnreadCount <= 0) {
      sessionStorage.removeItem(MESSAGE_TOAST_STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(
      MESSAGE_TOAST_STORAGE_KEY,
      JSON.stringify({ open: true, count: toastUnreadCount })
    );
  }, [showMessageToast, toastUnreadCount]);

  const handleMessageToastClose = (event, reason) => {
    if (reason === "clickaway") return;
    setShowMessageToast(false);
    setToastUnreadCount(0);
  };

  const handleMessageToastNavigate = () => {
    setShowMessageToast(false);
    navigate("/messages");
  };

  const toastMessageText =
    i18n.language === "es"
      ? `${toastUnreadCount} ${toastUnreadCount > 1 ? "mensajes nuevos recibidos" : "mensaje nuevo recibido"}`
      : `${toastUnreadCount} new message${toastUnreadCount > 1 ? "s" : ""} received`;

  return (
    <>
    <Drawer
      variant="permanent"
      sx={{
        width: mobile ? 260 : 210,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 230,
          boxSizing: 'border-box',
             backgroundColor: 'white',
          // backgroundColor: secondaryColor === '#FFFFFF' ? 'white' : secondaryColor,
          px: 0,
          borderRight: 'none',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          pl: 1,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, gap: 2 }}>
        <img  
         crossOrigin="anonymous"
          onError={() => setLogoToShow(defaultLogo)}
        src={logoToShow} style={{ width: 180, height: 70, objectFit: 'contain' }} alt="Logo" />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
        <List sx={{ py: 1 }}>
          {visibleMenuItems.map(item => {
            const isChildActive = item.children?.some(c => c.path === selectedPath);
            const isSelfActive = item.path === selectedPath;
const isOpen = openKey === item.key;
            return (
              <Box
                key={item.key}
                sx={{
                  // backgroundColor: isSelfActive
                  //   ? primaryColor
                  //   : isChildActive
                  //     ? getActiveBackgroundColor(primaryColor, 0.15)
                  //     : 'transparent',
                      backgroundColor: isChildActive ? getActiveBackgroundColor(primaryColor, 0.15) 
                      : isSelfActive ? primaryColor : 'transparent',

                  borderRadius: '6px',
                  mb: 1,
                }}
              >
                <ListItemButton
                  onClick={() => {
                    if (item.isLogout) {
                      // Handle logout
                      dispatch(logout());
                      toast.success(t("sidebar.logoutSuccess") || "Logged out successfully");
                      navigate('/');
                      return;
                    }
                    if (item.children) {
                      toggleMenu(item.key);
                      const firstChild = item.children[0];
                      if (firstChild) navigate(firstChild.path);
                    } else if (item.path) {
                      navigate(item.path);
                    }
                  }}
                  sx={{
                    position: 'relative',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: item.children ? '8px 8px 0 0' : '8px',
                    // '&:hover': {
                    //   backgroundColor: getHoverColor(secondaryColor, 0.1),
                    //   '& .MuiListItemText-primary': {
                    //     color: secondaryTextColor,
                    //   },
                    // },
                    '&:hover': {
                      backgroundColor: getHoverColor(secondaryColor, 0.1),
                      '& .MuiListItemText-primary': {
                        color: secondaryTextColor,
                      },
                      '& .MuiListItemIcon-root': {
                        '& img': {
                          filter: secondaryTextColor === '#FFFFFF'
                            ? 'brightness(0) invert(1)'
                            : 'brightness(0) invert(0)',
                        },
                        '& svg': {
                          color: secondaryTextColor,
                          fill: secondaryTextColor,
                        },
                      },
                      '& svg': {
                        color: secondaryTextColor,
                        fill: secondaryTextColor,
                      },
                      '& .MuiSvgIcon-root': {
                        color: secondaryTextColor,
                        fill: secondaryTextColor,
                      },
                    },

                  }}
                >
                  {/* <ListItemIcon sx={{ minWidth: 25, mr: 0.1 }}>
                    <img
                      src={isSelfActive && item.activeIcon ? item.activeIcon : item.icon}
                      alt={item.label}
                      style={{
                        width: 25,
                        height: 25,
                        filter: isSelfActive
                          ? primaryTextColor === '#FFFFFF'
                            ? 'brightness(0) invert(1)'
                            : 'none'
                          : 'none',
                      }}
                    />
                  </ListItemIcon> */}
                        <ListItemIcon sx={{ minWidth: 25, mr: 0.1 }}>
                     {item.isLogout ? (
                       <Logout
                         sx={{
                           color: isSelfActive ? primaryTextColor : '#44546F',
                           fontSize: 25,
                         }}
                       />
                     ) : (
                       <DynamicIcon
                         src={item.icon}
                         activeSrc={item.activeIcon}
                         isActive={isSelfActive}
                         textColor={isSelfActive ? primaryTextColor : '#44546F'}
                         size={25}
                         alt={item.label}
                         isHovered={hoveredItem === item.key}
                         hoverTextColor={secondaryTextColor}
                       />
                     )}
                   </ListItemIcon>

                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '14px',
                    color: isSelfActive ? primaryTextColor : '#44546F',

                      fontWeight: 500,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  />

                  {item.key === 'messages' && totalCount > 0 && (
                    <Chip
                      label={totalCount}
                      size="small"
                      sx={{
                        backgroundColor: secondaryColor,
                        color: secondaryTextColor,
                        fontSize: '10px',
                        height: '18px',
                        minWidth: '18px',
                        mr: 1,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
{/* 
                  {item.children &&
                    (openMenus[item.key]
                      ? <ExpandLess style={{ width: 20, height: 20, color: primaryTextColor }} />
                      : <ExpandMore style={{ width: 20, height: 20, color: primaryTextColor }} />)} */}

                  {item.children && (
                    <DynamicDropdownIcon
                      isOpen={isOpen}
                      textColor={isSelfActive ? primaryTextColor : '#44546F'}
                      size={20}
                      isHovered={hoveredItem === item.key}
                      hoverTextColor={secondaryTextColor}
                    />
                  )}
                  {isSelfActive && !item.children && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 5,
                        top: 13,
                        bottom: 8,
                        width: '3px',
                        height: '23px',
                        borderRadius: '3px',
                        backgroundColor: primaryTextColor,
                      }}
                    />
                  )}
                </ListItemButton>

                {item.children && (
                  <Collapse in={!!openMenus[item.key]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map(child => {
                        const isChildSelected = child.path === selectedPath;
                        return (
                          <ListItemButton
                            key={child.key}
                            onClick={() => navigate(child.path)}
                            sx={{
                              position: 'relative',
                              pl: 3,
                              py: 0.7,
                              gap: 1,
                              // backgroundColor: isChildSelected
                              //   ? getActiveBackgroundColor(primaryColor)
                              //   : 'transparent',
                              backgroundColor: isChildSelected 
                               ? primaryColor 
                                : 'transparent',
                              borderRadius: '0px 6px 6px 0px',
                              '&:hover': {
                                backgroundColor: getHoverColor(secondaryColor, 0.1),
                              },
                            }}
                          >
                            {isChildSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 12,
                                  bottom: 6,
                                  width: '3px',
                                  backgroundColor: primaryTextColor,
                                  borderRadius: '2px',
                                  height: '23px',
                                }}
                              />
                            )}
                            {/* <Box
                              component="img"
                              src={isChildSelected ? active : sidebaricon1}
                              alt="submenu icon"
                              sx={{ width: 10, height: 10, ml: 1, objectFit: 'contain' }}
                            /> */}
                            <DynamicIcon
  src={sidebaricon1}
  activeSrc={active}
  isActive={isChildSelected}
  textColor={isChildSelected ? primaryTextColor : '#44546F'}
  size={10}
  alt="submenu icon"
  isHovered={hoveredItem === child.key}
  hoverTextColor={secondaryTextColor}
/>
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color: isChildSelected ? primaryTextColor : '#44546F',
                                fontFamily: 'Poppins, sans-serif',
                              }}
                            />
                            {child.key === 'messages_inbox' && totalCount > 0 && (
                              <Chip
                                label={totalCount}
                                size="small"
                                sx={{
                                  backgroundColor: secondaryColor,
                                  color: secondaryTextColor,
                                  fontSize: '10px',
                                  height: '18px',
                                  minWidth: '18px',
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
      </Box>
    </Drawer>
    <Snackbar
      open={showMessageToast}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      onClose={handleMessageToastClose}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<Chat fontSize="small" />}
        onClick={handleMessageToastNavigate}
        sx={{
          cursor: "pointer",
          alignItems: "center",
          ".MuiAlert-message": { py: 0.5 },
        }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              handleMessageToastClose();
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      >
        {toastMessageText}
      </Alert>
    </Snackbar>
    </>
  );
};

export default Sidebar;







// import React, { useMemo, useState, useEffect } from 'react';
// import {
//   Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Chip,
// } from '@mui/material';
// import { ExpandLess, ExpandMore } from '@mui/icons-material';
// import { useNavigate, useLocation } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';
// import { useSelector } from 'react-redux';

// // icons ...
// import dashboardicon from '../assets/dashboardicon.svg';
// import activedashboard from "../assets/activedashboard.svg";
// import attendance from "../assets/Sidebar/attendance.svg";
// import taskmanagement from "../assets/Sidebar/taskmanagement.svg";
// import requestmanagement from "../assets/Sidebar/requestmanagement.svg";
// import documents from "../assets/Sidebar/documents.svg";
// import reports from "../assets/Sidebar/reports.svg";
// import message from "../assets/Sidebar/message.svg";
// import payments from "../assets/Sidebar/payments.svg";
// import activerequest from '../assets/activerequest.svg';
// import notifications from "../assets/Sidebar/notifications.svg";
// import accountsettigs from "../assets/Sidebar/accountsettings.svg";
// import sidebaricon1 from '../assets/sidebaricon1.svg';
// import sidebaricon2 from '../assets/sidebaricon2.svg';
// import sidebaricon3 from '../assets/sidebaricon3.svg';
// import sidebaricon4 from '../assets/sidebaricon4.svg';
// import sidebaricon6 from '../assets/sidebaricon6.svg';
// import sidebaricon7 from '../assets/sidebaricon7.svg';
// import sidebaricon8 from '../assets/sidebaricon8.svg';
// import activenotifications from "../assets/activenotifications.svg";
// import activereports from "../assets/activereports.svg";
// import activesetting from "../assets/activesettings.svg";
// import active from '../assets/active.svg';
// import websitelogo from "../assets/websitelogo.png";
// import websitelogoes from "../assets/websitelogoes.png";


// const Sidebar = ({ mobile = false }) => {
//   const { t, i18n } = useTranslation();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { totalCount } = useSelector((state) => state.messageCount);

//   // Build menu with labels from i18n but STABLE keys for logic
// // Build menu with labels from i18n but STABLE, UNIQUE keys for logic
// const menuItems = useMemo(() => [
//   {
//     key: 'dashboard_root',
//     label: t("sidebar.dashboard"),
//     icon: dashboardicon,
//     activeIcon: activedashboard,
//     path: '/dashboard',
//   },

//   {
//     key: 'attendance',
//     label: t("sidebar.attendance"),
//     icon: attendance,
//     children: [
//       { key: 'attendance_today_punch',   label: t("sidebar.todayPunch"),     path: '/today-punch' },
//       { key: 'attendance_monthly_summary', label: t("sidebar.monthlySummary"), path: '/monthly-summary' },
//     ],
//   },

//   {
//     key: 'task_management',
//     label: t("sidebar.taskManagement"),
//     icon: taskmanagement,
//     children: [
//       { key: 'task_my_tasks',    label: t("sidebar.myTasks"),     path: '/my-tasks' },
//       { key: 'task_calendar_view', label: t("sidebar.calendarView"), path: '/calender-view' },
//     ],
//   },

//   {
//     key: 'request_management',
//     label: t("sidebar.requestManagement"),
//     icon: requestmanagement,
//     activeIcon: activerequest,
//     path: '/request-management',
//   },

//   {
//     key: 'payments',
//     label: t("sidebar.payments"),
//     icon: payments,
//     children: [
//       { key: 'payments_expenses',     label: t("sidebar.expenses"),     path: '/expenses' },
//       { key: 'payments_remuneration', label: t("sidebar.remuneration"), path: '/remuneration' },
//     ],
//   },

//   {
//     key: 'messages',
//     label: t("sidebar.messages"),
//     icon: message,
//     children: [
//       { key: 'messages_inbox',   label: t("sidebar.companyAdmin"),   path: '/messages' },
//       // { key: 'messages_support', label: t("sidebar.support"), path: '/commission-record' },
//     ],
//   },

//   {
//     key: 'documents',
//     label: t("sidebar.document"),
//     icon: documents,
//     children: [
//       { key: 'documents_mine',       label: t("sidebar.myDocuments"),   path: '/my-documents' },
//       { key: 'documents_companies',  label: t("sidebar.companiesDocs"), path: '/company-documents' },
//     ],
//   },
   

//   {
//     key: 'reports',
//     label: t("sidebar.reportStatistics"),
//     icon: reports,
//     activeIcon: activereports,
//     path: '/reports-services',
//   },

//   {
//     key: 'notifications',
//     label: t("sidebar.notifications"),
//     icon: notifications,
//     activeIcon: activenotifications,
//     path: '/notifications',
//   },

//   {
//     key: 'settings',
//     label: t("sidebar.accountSettings"),
//     icon: accountsettigs,
//     activeIcon: accountsettigs,
//     path: '/settings',
//   },
// ], [t]);


//   // Track open parent menus by STABLE key (not label)
// const [openMenus, setOpenMenus] = useState({});

//   const selectedPath = location.pathname;

//   // Auto-open the parent for the current route; re-run on route or language change
// // Auto-open the parent for the current route; re-run on route or language change
// useEffect(() => {
//   const parent = menuItems.find(
//     (item) => item.children && item.children.some((c) => c.path === selectedPath)
//   );

//   if (parent) {
//     setOpenMenus((prev) => ({ ...prev, [parent.key]: true }));
//   }
// }, [selectedPath, menuItems]);

// const toggleMenu = (key) => {
//   setOpenMenus((prev) => ({
//     ...prev,
//     [key]: !prev[key], // toggle current
//   }));
// };
//   const language = i18n.language === 'es' ? 'Spanish' : 'English';
//   const logoPath = language === 'English' ? websitelogoes : websitelogo;
//   useEffect(() => {
//     const parent = menuItems.find(
//       (item) => item.children && item.children.some((c) => c.path === selectedPath)
//     );
  
//     if (parent) {
//       setOpenMenus({ [parent.key]: true }); 
//     } else {
//       setOpenMenus({});   
//     }
//   }, [selectedPath, menuItems]);
//   return (
//     <Drawer
//       variant="permanent"
//       sx={{
//         width: mobile ? 260 : 210,
//         flexShrink: 0,
//         '& .MuiDrawer-paper': {
//           width: 230,
//           boxSizing: 'border-box',
//           backgroundColor: 'white',
//           px: 0,
//           borderRight: 'none',
//           boxShadow: 'none',
//           display: 'flex',
//           flexDirection: 'column',
//           pl: 1,
//         },
//       }}
//     >
//       <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, gap: 2, flexShrink: 0, backgroundColor: 'white', zIndex: 10 }}>
//         <img src={logoPath} style={{ width: 180, height: 70, objectFit: 'contain' }} />
//       </Box>

//       <Box sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
//         <List sx={{ py: 1 }}>
//           {menuItems.map(item => {
//             const isChildActive = 'children' in item && item.children.some(c => c.path === selectedPath);
//             const isSelfActive = 'path' in item && item.path === selectedPath;

//             return (
//               <Box
//                 key={item.key}
//                 sx={{
//                   backgroundColor: isChildActive ? '#F0F9FF' : isSelfActive ? 'rgba(209, 233, 251, 0.58)' : 'transparent',
//                   borderRadius: '6px',
//                   mb: 1,
//                 }}
//               >
//                 <ListItemButton
//                   onClick={() => {
//                     if ('children' in item) {
//                       toggleMenu(item.key);
//                       // optional: navigate to first child on open
//                       const firstChild = item.children[0];
//                       if (firstChild) navigate(firstChild.path);
//                     } else if ('path' in item && item.path) {
//                       navigate(item.path);
//                     }
//                   }}
//                   sx={{
//                     position: 'relative',
//                     gap: 1,
//                     px: 2,
//                     py: 1,
//                     borderRadius: 'children' in item ? '8px 8px 0 0' : '8px',
//                     '&:hover': { backgroundColor: '#F0F9FF' },
//                   }}
//                 >
//                   <ListItemIcon sx={{ minWidth: 25, mr: 0.1 }}>
//                     <img
//                       src={isSelfActive && item.activeIcon ? item.activeIcon : item.icon}
//                       alt={item.label}
//                       style={{ width: 25, height: 25 }}
//                     />
//                   </ListItemIcon>

//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: '14px',
//                       color: isSelfActive ? '#006EC2' : '#44546F',
//                       fontWeight: 500,
//                       fontFamily: 'Poppins, sans-serif',
//                     }}
//                   />

//                   {item.key === 'messages' && totalCount > 0 && (
//                     <Chip
//                       label={totalCount}
//                       size="small"
//                       sx={{
//                         backgroundColor: '#006EC2',
//                         color: 'white',
//                         fontSize: '10px',
//                         height: '18px',
//                         minWidth: '18px',
//                         mr: 1,
//                         '& .MuiChip-label': {
//                           px: 0.5,
//                         },
//                       }}
//                     />
//                   )}

//                   {'children' in item && (openMenus[item.key] ? <ExpandLess style={{ width: 20, height: 20 }} /> : <ExpandMore style={{ width: 20, height: 20 }} />)}

//                   {isSelfActive && !('children' in item) && (
//                     <Box
//                       sx={{
//                         position: 'absolute',
//                         left: 5,
//                         top: 13,
//                         bottom: 8,
//                         width: '3px',
//                         height: '23px',
//                         borderRadius: '3px',
//                         backgroundColor: '#006EC2',
//                       }}
//                     />
//                   )}
//                 </ListItemButton>

//                 {'children' in item && (
//                   <Collapse in={!!openMenus[item.key]} timeout="auto" unmountOnExit>
//                     <List component="div" disablePadding>
//                       {item.children.map(child => {
//                         const isChildSelected = child.path === selectedPath;
//                         return (
//                           <ListItemButton
//                             key={child.key}
//                             onClick={() => navigate(child.path)}
//                             sx={{
//                               position: 'relative',
//                               pl: 3,
//                               py: 0.7,
//                               gap: 1,
//                               backgroundColor: isChildSelected ? '#F0F9FF' : 'transparent',
//                               borderRadius: '0px 6px 6px 0px',
//                               '&:hover': { backgroundColor: '#F3F7FD' },
//                             }}
//                           >
//                             {isChildSelected && (
//                               <Box
//                                 sx={{
//                                   position: 'absolute',
//                                   left: 0,
//                                   top: 12,
//                                   bottom: 6,
//                                   width: '3px',
//                                   backgroundColor: '#006EC2',
//                                   borderRadius: '2px',
//                                   height: '23px',
//                                 }}
//                               />
//                             )}
//                             <Box component="img" src={isChildSelected ? active : sidebaricon1} alt="submenu icon" sx={{ width: 10, height: 10, ml: 1, objectFit: 'contain' }} />
//                             <ListItemText
//                               primary={child.label}
//                               primaryTypographyProps={{
//                                 fontSize: '14px',
//                                 fontWeight: 400,
//                                 color: isChildSelected ? '#006EC2' : '#44546F',
//                                 fontFamily: 'Poppins, sans-serif',
//                               }}
//                             />
//                             {child.key === 'messages_inbox' && totalCount > 0 && (
//                               <Chip
//                                 label={totalCount}
//                                 size="small"
//                                 sx={{
//                                   backgroundColor: '#006EC2',
//                                   color: 'white',
//                                   fontSize: '10px',
//                                   height: '18px',
//                                   minWidth: '18px',
//                                   '& .MuiChip-label': {
//                                     px: 0.5,
//                                   },
//                                 }}
//                               />
//                             )}
//                           </ListItemButton>
//                         );
//                       })}
//                     </List>
//                   </Collapse>
//                 )}
//               </Box>
//             );
//           })}
//         </List>
//       </Box>
//     </Drawer>
//   );
// };

// export default Sidebar;

